import * as React from 'react'
import type { LucideIcon } from 'lucide-react'

import { cn } from '@/lib/utils'

/**
 * Aurora empty state — for "nothing here yet" surfaces. Replaces the
 * grey dashed-border boxes we had scattered across pages with a single
 * organized affordance: icon (optional), title, description, and
 * call-to-action slot.
 *
 * Layout is centered + readable; the container caps width so the copy
 * doesn't go edge-to-edge on a wide page.
 */
interface EmptyStateProps extends React.ComponentProps<'div'> {
  icon?: LucideIcon
  title: string
  description?: React.ReactNode
  action?: React.ReactNode
  /** Use `bordered` (default) for stand-alone cards, `bare` inside other surfaces. */
  variant?: 'bordered' | 'bare'
}

function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  variant = 'bordered',
  className,
  ...props
}: EmptyStateProps) {
  return (
    <div
      data-slot="empty-state"
      className={cn(
        'flex flex-col items-center gap-3 px-6 py-10 text-center',
        variant === 'bordered' &&
          'rounded-lg border border-dashed border-border bg-card/40',
        className,
      )}
      {...props}
    >
      {Icon ? (
        <div
          aria-hidden
          className="flex size-11 items-center justify-center rounded-full bg-primary-soft text-primary-soft-foreground"
        >
          <Icon className="size-5" />
        </div>
      ) : null}
      <div className="max-w-md space-y-1.5">
        <h3 className="text-sm font-semibold tracking-tight text-foreground">
          {title}
        </h3>
        {description ? (
          <p className="text-sm leading-relaxed text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>
      {action ? <div className="pt-1">{action}</div> : null}
    </div>
  )
}

export { EmptyState }
